import { useEffect, useRef } from 'react';
import { gsap } from '../lib/gsapContext';

const days = ['M', 'T', 'W', 'T', 'F'];

export default function FeatureScheduler() {
  const root = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const cursor = root.current!.querySelector('[data-cursor]');
      const target = root.current!.querySelector('[data-target]') as HTMLElement;
      const save = root.current!.querySelector('[data-save]') as HTMLElement;
      if (!cursor || !target || !save) return;

      const loop = () => {
        const tRect = target.getBoundingClientRect();
        const sRect = save.getBoundingClientRect();
        const cRect = root.current!.getBoundingClientRect();

        const tl = gsap.timeline({
          repeat: -1,
          repeatDelay: 1.2,
          defaults: { ease: 'power3.inOut' },
        });
        tl.set(cursor, { x: 30, y: 30, opacity: 0 })
          .to(cursor, { opacity: 1, duration: 0.3 })
          .to(cursor, {
            x: tRect.left - cRect.left + tRect.width / 2 - 8,
            y: tRect.top - cRect.top + tRect.height / 2 - 8,
            duration: 0.9,
          })
          .call(() => target.classList.add('!bg-gold', '!text-black'))
          .to({}, { duration: 0.5 })
          .to(cursor, {
            x: sRect.left - cRect.left + sRect.width / 2 - 8,
            y: sRect.top - cRect.top + sRect.height / 2 - 8,
            duration: 0.9,
          })
          .call(() => save.classList.add('!bg-gold', '!text-black'))
          .to({}, { duration: 0.6 })
          .call(() => {
            target.classList.remove('!bg-gold', '!text-black');
            save.classList.remove('!bg-gold', '!text-black');
          });
      };
      // delay until layout settles
      const id = setTimeout(loop, 400);
      return () => clearTimeout(id);
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <article
      ref={root}
      className="grain group relative flex h-[420px] flex-col overflow-hidden rounded-4xl border border-white/10 bg-charcoal p-8"
    >
      <p className="label">Engagement Cadence</p>
      <h3 className="mt-6 font-display text-3xl font-light text-white">
        Structured rhythm
      </h3>

      <div className="relative mt-6 flex-1">
        <div className="grid grid-cols-5 gap-2">
          {days.map((d, i) => (
            <div
              key={i}
              data-target={i === 2 ? true : undefined}
              className="flex h-12 items-center justify-center rounded-xl border border-white/10 bg-black/40 font-mono text-xs text-white/85 transition-colors duration-300"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-5 gap-2">
          {Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="h-6 rounded-md border border-white/5 bg-black/30"
            />
          ))}
        </div>

        <button
          data-save
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 px-5 py-2 font-sans text-[0.7rem] uppercase tracking-label text-white/90 transition-colors"
        >
          Save Schedule
        </button>

        <div
          data-cursor
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 h-4 w-4"
        >
          <svg viewBox="0 0 16 16" className="h-full w-full text-gold">
            <path
              fill="currentColor"
              d="M2 2l4 12 2-5 5-2L2 2z"
              stroke="#0A0A0A"
              strokeWidth="0.6"
            />
          </svg>
        </div>
      </div>
    </article>
  );
}
